import React from 'react';
import Box from '@mui/material/Box';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { makeStyles } from '@material-ui/core/styles';
import { UITestTypeCoverage } from '../../../../../../../api/teamMetricsApi';

const useStyles = makeStyles((theme) => ({
  container: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    minWidth: 180,
  },
  barContainer: {
    flex: 1,
    height: 16,
    backgroundColor: '#E2E8F0',
    borderRadius: 4,
    overflow: 'hidden',
    position: 'relative',
  },
  segment: {
    height: '100%',
    position: 'absolute',
    top: 0,
    left: 0,
    transition: 'width 0.3s ease',
  },
  unitSegment: {
    backgroundColor: '#3182CE',
    zIndex: 1,
  },
  tasSegment: {
    backgroundColor: '#38A169',
    zIndex: 2,
  },
  overlapSegment: {
    zIndex: 3,
    background: `repeating-linear-gradient(
      -45deg,
      #3182CE,
      #3182CE 3px,
      #38A169 3px,
      #38A169 6px
    )`,
  },
  percentage: {
    fontSize: 12,
    fontWeight: 600,
    color: '#4A5568',
    minWidth: 45,
    textAlign: 'right',
    fontFamily: 'monospace',
  },
  naText: {
    fontSize: 12,
    fontWeight: 500,
    color: '#A0AEC0',
    fontStyle: 'italic',
  },
  tooltipContent: {
    padding: theme.spacing(1),
  },
  tooltipRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(0.5),
    '&:last-child': {
      marginBottom: 0,
    },
  },
  tooltipDot: {
    width: 8,
    height: 8,
    borderRadius: '50%',
  },
  tooltipLabel: {
    fontSize: 12,
    fontWeight: 500,
    color: '#E2E8F0',
    minWidth: 70,
  },
  tooltipValue: {
    fontSize: 12,
    fontWeight: 600,
    color: '#fff',
    fontFamily: 'monospace',
  },
}));

interface CoverageProgressBarProps {
  coverage: UITestTypeCoverage[];
  showPercentage?: boolean;
}

export const CoverageProgressBar: React.FC<CoverageProgressBarProps> = ({
  coverage,
  showPercentage = true,
}) => {
  const classes = useStyles();

  const unitCoverage = coverage.find((c) => c.testType === 'go_unit');
  const tasCoverage = coverage.find((c) => c.testType === 'go_tas');
  const combinedCoverage = coverage.find((c) => c.testType === 'go_combined');

  const totalUnits = combinedCoverage?.totalUnits ?? unitCoverage?.totalUnits ?? tasCoverage?.totalUnits;

  if (totalUnits === null || totalUnits === undefined || totalUnits === 0) {
    return (
      <Box className={classes.container}>
        <Typography className={classes.naText}>N/A</Typography>
      </Box>
    );
  }

  const unitCovered = unitCoverage?.coveredUnits ?? 0;
  const tasCovered = tasCoverage?.coveredUnits ?? 0;
  const combinedCovered = combinedCoverage?.coveredUnits ?? 0;

  const unitPercent = (unitCovered / totalUnits) * 100;
  const combinedPercent = (combinedCovered / totalUnits) * 100;

  // Calculate overlap: the portion covered by BOTH unit and TAS
  // Overlap = unit + tas - combined (since combined is the union)
  const overlapCovered = Math.max(0, unitCovered + tasCovered - combinedCovered);
  const overlapPercent = (overlapCovered / totalUnits) * 100;

  // Unit-only portion (not covered by TAS)
  const unitOnlyPercent = unitPercent - overlapPercent;

  const formatUnits = (value: number | null) => {
    if (value === null) return 'N/A';
    return value.toLocaleString();
  };

  const formatPercentage = (covered: number | null, total: number | null) => {
    if (covered === null || total === null || total === 0) return 'N/A';
    return `${((covered / total) * 100).toFixed(1)}%`;
  };

  const tooltipContent = (
    <Box className={classes.tooltipContent}>
      <Box className={classes.tooltipRow}>
        <Box className={classes.tooltipDot} style={{ backgroundColor: '#3182CE' }} />
        <Typography className={classes.tooltipLabel}>Unit:</Typography>
        <Typography className={classes.tooltipValue}>
          {formatUnits(unitCovered)} / {formatUnits(totalUnits)} ({formatPercentage(unitCovered, totalUnits)})
        </Typography>
      </Box>
      <Box className={classes.tooltipRow}>
        <Box className={classes.tooltipDot} style={{ backgroundColor: '#38A169' }} />
        <Typography className={classes.tooltipLabel}>TAS:</Typography>
        <Typography className={classes.tooltipValue}>
          {formatUnits(tasCovered)} / {formatUnits(totalUnits)} ({formatPercentage(tasCovered, totalUnits)})
        </Typography>
      </Box>
      <Box className={classes.tooltipRow}>
        <Box className={classes.tooltipDot} style={{ backgroundColor: '#805AD5' }} />
        <Typography className={classes.tooltipLabel}>Combined:</Typography>
        <Typography className={classes.tooltipValue}>
          {formatUnits(combinedCovered)} / {formatUnits(totalUnits)} ({formatPercentage(combinedCovered, totalUnits)})
        </Typography>
      </Box>
    </Box>
  );

  return (
    <Tooltip title={tooltipContent} arrow placement="top">
      <Box className={classes.container}>
        <Box className={classes.barContainer}>
          {/* Unit-only portion (blue) - from 0 to unitOnlyPercent */}
          {unitOnlyPercent > 0 && (
            <Box
              className={`${classes.segment} ${classes.unitSegment}`}
              style={{ width: `${Math.min(unitOnlyPercent, 100)}%` }}
            />
          )}
          {/* Overlap portion (striped) - from unitOnlyPercent to unitOnlyPercent + overlapPercent */}
          {overlapPercent > 0 && (
            <Box
              className={`${classes.segment} ${classes.overlapSegment}`}
              style={{
                left: `${unitOnlyPercent}%`,
                width: `${Math.min(overlapPercent, 100 - unitOnlyPercent)}%`,
              }}
            />
          )}
          {/* TAS-only portion (green) - from unitPercent to combinedPercent */}
          {combinedPercent > unitPercent && (
            <Box
              className={`${classes.segment} ${classes.tasSegment}`}
              style={{
                left: `${unitPercent}%`,
                width: `${Math.min(combinedPercent - unitPercent, 100 - unitPercent)}%`,
              }}
            />
          )}
        </Box>
        {showPercentage && (
          <Typography className={classes.percentage}>
            {combinedCoverage?.coveragePercentage?.toFixed(1) ?? combinedPercent.toFixed(1)}%
          </Typography>
        )}
      </Box>
    </Tooltip>
  );
};

export default CoverageProgressBar;
